import React, { useCallback, useMemo, useState, useRef } from 'react';
import { Autocomplete, ClassNameMap } from '@mui/material';
import { InputBase, Popper, InputAdornment, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SearchIcon from '@mui/icons-material/Search';
import { grey } from '@mui/material/colors';
import {
  AutocompleteRenderInputParams,
  createFilterOptions,
  AutocompleteCloseReason
} from '@mui/material/Autocomplete';
import { FormattedMessage } from 'react-intl';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import {
  MORE_OPTIONS_ID,
  useMoreMessage
} from 'modules/common/hooks/useMoreMessage';
import { LabelValue } from 'graphql/types';
import { SimpleChip } from './SimpleChip';

type SearchableChipProps<T> = {
  children?: React.ReactNode;
  label?: TimesheetLabel | null;
  options: T[];
  loading: boolean;
  onSelect: (value: T) => void;
  onOpen: () => void;
  onClose?: () => void;
  handleSearch?: (query: string) => void;
  disabled?: boolean;
  renderOption?: (option: T, searchText: string) => React.ReactNode;
  getOptionDisabled?: (option: T) => boolean;
  getOptionLabel: (option: T) => string;
  handleDelete?: () => void;
  getOptionSelected?: (option: T, selected: T) => boolean;
  hasMore?: boolean;
  acceptSuggestion?: (value: LabelValue) => void;
  overrideClasses?: ClassNameMap;
  value?: T;
};

type StyleProps = {
  hasMore?: boolean;
  classes?: ClassNameMap;
};

const useSearchableStyles = makeStyles((theme) => ({
  paper: {
    boxShadow: 'none',
    margin: 0
  },
  option: {
    minHeight: 'auto',
    padding: theme.spacing(1, 2)
  },
  popper: {
    boxShadow: theme.shadows[4],
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(0.5)
  },
  listbox: {
    maxHeight: theme.spacing(22),
    '& li:last-child': {
      opacity: ({ hasMore }: StyleProps) => (hasMore ? 1 : undefined)
    }
  },
  popperDisablePortal: {
    position: 'relative',
    borderRadius: theme.spacing(0, 0, 0.5, 0.5)
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    display: 'flex',
    alignSelf: 'center',
    margin: theme.spacing(0, 0, 0, 2),
    padding: theme.spacing(2, 0, 2, 2),
    backgroundColor: grey[100]
  },
  root: {},
  input: {
    margin: theme.spacing(2, 2, 2, 0),
    backgroundColor: grey[100],
    borderRadius: theme.spacing(0.5)
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column'
  }
}));

const useShowMoreClasses = makeStyles((theme) => ({
  root: {
    fontStyle: 'italic',
    fontSize: theme.typography.caption.fontSize,
    color: '#666666'
  }
}));

const loadingText = (
  <Typography data-testid="searchable-chip-loading">
    <FormattedMessage id="searchableChip.loading" />
  </Typography>
);

const listBoxProps = { 'data-testid': 'search-listbox' } as any;

interface PopperComponentProps {
  anchorEl?: any;
  disablePortal?: boolean;
  open: boolean;
}

const popperComponent = (props: PopperComponentProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { disablePortal, anchorEl, open, ...other } = props;
  return <div {...other} />;
};

export const SearchableChip = <T,>({
  children,
  label,
  options = [],
  loading,
  onOpen,
  onClose,
  handleSearch,
  onSelect,
  disabled = false,
  renderOption,
  getOptionLabel,
  getOptionDisabled,
  handleDelete,
  getOptionSelected,
  hasMore,
  value,
  acceptSuggestion,
  overrideClasses
}: SearchableChipProps<T>): React.ReactElement => {
  const [searchText, setSearchText] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const classes = useSearchableStyles({ hasMore, classes: overrideClasses });
  const moreItemsClasses = useShowMoreClasses();
  const { message } = useMoreMessage(label);

  const chipRef = useRef<HTMLDivElement | null>(null);

  const checkOptionDisabled = useCallback(
    (option: any) => {
      if (option?.id === MORE_OPTIONS_ID) {
        return true;
      }
      if (getOptionDisabled) {
        return getOptionDisabled(option);
      }
      return false;
    },
    [getOptionDisabled]
  );

  const handleClick = useCallback(
    (event: any) => {
      setAnchorEl((previousVal) => {
        if (previousVal) return null;
        return event.currentTarget;
      });
      if (!open) {
        setSearchText('');
        onOpen();
      }
    },
    [setSearchText, onOpen, setAnchorEl, open]
  );

  const onSearchChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = ev?.target.value;
      setSearchText(value);
      if (handleSearch) handleSearch(value);
    },
    [setSearchText, handleSearch]
  );

  const onClickAway = useCallback(
    (event: React.ChangeEvent<any>, reason: AutocompleteCloseReason) => {
      if (reason === 'toggleInput') return;
      if (onClose) onClose();
      chipRef.current?.focus();
      setAnchorEl(null);
    },
    [setAnchorEl, onClose]
  );

  const inputAdornment = useMemo(
    () => (
      <InputAdornment position="start" className={classes.searchIcon}>
        <SearchIcon />
      </InputAdornment>
    ),
    [classes.searchIcon]
  );

  const onChange = useCallback(
    (element, value) => {
      setAnchorEl(null);
      onSelect(value);
    },
    [setAnchorEl, onSelect]
  );

  const autocompleteClasses = useMemo(
    () => ({
      paper: classes.paper,
      option: classes.option,
      popper: classes.popper,
      root: classes.root,
      input: classes.input,
      popperDisablePortal: classes.popperDisablePortal,
      listbox: classes.listbox
    }),
    [classes]
  );

  const getRenderOption = useCallback(
    (props, option) => {
      if (option?.id === MORE_OPTIONS_ID) {
        return (
          <li {...props}>
            <Typography classes={moreItemsClasses}>{option.name}</Typography>
          </li>
        );
      }
      if (renderOption)
        return <li {...props}>{renderOption(option, searchText)}</li>;
    },
    [moreItemsClasses, renderOption, searchText]
  );

  const _renderOption = useMemo(() => {
    return { renderOption: getRenderOption };
  }, [getRenderOption]);

  const getInputComponent = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <div className={classes.inputContainer} ref={params.InputProps.ref}>
        <InputBase
          onChange={onSearchChange}
          inputProps={{
            ...params.inputProps,
            'data-testid': 'search-input'
          }}
          autoFocus
          startAdornment={inputAdornment}
        />

        {children}
      </div>
    ),
    [classes.inputContainer, inputAdornment, onSearchChange, children]
  );

  const highlightProps = useMemo(
    () => ({
      value: value,
      isOptionEqualToValue: getOptionSelected
    }),
    [getOptionSelected, value]
  );

  const filterOptions = useCallback(
    (options, state) => {
      const defaultFilterOptions = createFilterOptions();
      const filteredOptions = defaultFilterOptions(options, state);
      hasMore && filteredOptions.push(message);
      return filteredOptions as T[];
    },
    [hasMore, message]
  );

  return (
    <>
      <SimpleChip
        label={label}
        onChipClick={handleClick}
        handleDelete={handleDelete}
        disabled={disabled}
        chipRef={chipRef}
        acceptSuggestion={acceptSuggestion}
      />
      <Popper
        open={open}
        data-testid="searchable-popper"
        placement="bottom-start"
        className={classes.popper}
        anchorEl={anchorEl}
      >
        <Autocomplete
          onClose={onClickAway}
          loadingText={loadingText}
          data-testid="searchable-chip"
          options={options}
          inputValue={searchText}
          getOptionLabel={getOptionLabel}
          PopperComponent={popperComponent}
          getOptionDisabled={checkOptionDisabled}
          {..._renderOption}
          open
          loading={loading}
          classes={autocompleteClasses}
          disablePortal
          onChange={onChange}
          renderInput={getInputComponent}
          {...highlightProps}
          filterOptions={filterOptions}
          ListboxProps={listBoxProps}
        />
      </Popper>
    </>
  );
};
